import { Button, Col, Drawer, Input, Row, Spin, Statistic, Switch, Table, Tag, Tooltip } from "antd";
import { BarChartOutlined, EyeOutlined, LinkOutlined, MenuOutlined, ReloadOutlined } from "@ant-design/icons";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import deleteWhiteIcon from "../../../assets/images/icon/deleteWhiteIcon.png";
import EditIcon from "../../../assets/images/edit.svg";
import Plus from "../../../assets/images/plus.svg";
import DeleteModal from "../../../components/DeleteModal";
import SectionWrapper from "../../../components/SectionWrapper";
import ShowTotal from "../../../components/ShowTotal";
import { AppStateContext } from "../../../context/AppContext";
import apiPath from "../../../constants/apiPath";
import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useDebounce from "../../../hooks/useDebounce";
import useRequest from "../../../hooks/useRequest";
import AddForm from "./AddForm";
import ScrapeProductModal from "./ScrapeProductModal";

const normalizeOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      value: item?._id || item?.id || item?.value || item,
      label: item?.name || item?.title || item?.slug || item?.value || item,
      raw: item,
    }))
    .filter((item) => item.value);

function Index() {
  const heading = "Products Management";
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const navigate = useNavigate();
  const requestRef = useRef(request);

  const api = {
    addEdit: apiPath.product,
    list: apiPath.product,
    categories: apiPath.common.categories,
    subCategories: apiPath.common.subCategories,
    occasions: apiPath.common.getOccasion,
    keywords: apiPath.keywordEmotion,
    digitalDocuments: apiPath.digitalDocuments,
    scraper: apiPath.scraper,
    affiliateRuns: apiPath.affiliateRuns,
    affiliateStatus: apiPath.affiliateStatus,
  };

  const [searchText, setSearchText] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [occasionOptions, setOccasionOptions] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [digitalDocumentOptions, setDigitalDocumentOptions] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [scrapeVisible, setScrapeVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [deleteModal, showDeleteModal] = useState(false);
  const [affiliateRunning, setAffiliateRunning] = useState(false);
  const [actionsVisible, setActionsVisible] = useState(false);
  const [reportsVisible, setReportsVisible] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [affiliateReport, setAffiliateReport] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const debouncedSearchText = useDebounce(searchText, 300);

  const truncateTitle = (value) => {
    const title = String(value || "");
    return title.length > 100 ? `${title.slice(0, 100)}...` : title || "-";
  };

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const fetchCategories = useCallback(() => {
    requestRef.current({
      url: api.categories,
      method: "GET",
      onSuccess: ({ data }) => {
        setCategoryOptions(normalizeOptions(data?.docs || data));
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load categories", Severty.ERROR);
      },
    });
  }, [api.categories]);

  const fetchSubCategories = useCallback(() => {
    requestRef.current({
      url: api.subCategories,
      method: "GET",
      onSuccess: ({ data }) => {
        setSubCategoryOptions(normalizeOptions(data?.docs || data));
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load sub categories", Severty.ERROR);
      },
    });
  }, [api.subCategories]);

  const fetchOccasions = useCallback(() => {
    requestRef.current({
      url: api.occasions,
      method: "GET",
      onSuccess: ({ data }) => {
        setOccasionOptions(normalizeOptions(data?.docs || data));
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load occasions", Severty.ERROR);
      },
    });
  }, [api.occasions]);

  const fetchKeywords = useCallback(() => {
    requestRef.current({
      url: api.keywords,
      method: "GET",
      onSuccess: ({ data }) => {
        setKeywordOptions(normalizeOptions(data?.docs || data));
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load keywords", Severty.ERROR);
      },
    });
  }, [api.keywords]);

  const fetchDigitalDocuments = useCallback(() => {
    requestRef.current({
      url: `${api.digitalDocuments}?page=1&pageSize=100&status=active`,
      method: "GET",
      onSuccess: ({ data }) => {
        setDigitalDocumentOptions(normalizeOptions(data?.docs || data));
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load digital documents", Severty.ERROR);
      },
    });
  }, [api.digitalDocuments]);

  const fetchData = useCallback(
    (pageConfig = { current: 1, pageSize: 10 }) => {
      setLoading(true);

      requestRef.current({
        url: `${api.list}?page=${pageConfig?.current || 1}&pageSize=${pageConfig?.pageSize || 10}&search=${debouncedSearchText}`,
        method: "GET",
        onSuccess: ({ data }) => {
          setLoading(false);
          setList(
            (data?.docs || []).map((item) => ({
              ...item,
              key: item._id,
            })),
          );
          setPagination((prev) => ({
            ...prev,
            current: pageConfig?.current || 1,
            pageSize: pageConfig?.pageSize || 10,
            total: data?.totalDocs || 0,
          }));
        },
        onError: (error) => {
          setLoading(false);
          ShowToast(error?.response?.data?.message || error?.message || "Failed to load products", Severty.ERROR);
        },
      });
    },
    [api.list, debouncedSearchText],
  );

  useEffect(() => {
    setPageHeading(heading);
    fetchCategories();
    fetchSubCategories();
    fetchOccasions();
    fetchKeywords();
    fetchDigitalDocuments();
  }, [fetchCategories, fetchDigitalDocuments, fetchKeywords, fetchOccasions, fetchSubCategories, heading, setPageHeading]);

  useEffect(() => {
    fetchData({ current: 1, pageSize: pagination.pageSize });
  }, [debouncedSearchText, fetchData, pagination.pageSize, refresh]);

  const normalizeIds = (items = []) => (Array.isArray(items) ? items.map((item) => item?._id || item?.id || item?.value || item).filter(Boolean) : []);

  const handleChangeStatus = (record) => {
    requestRef.current({
      url: `${api.addEdit}/${record?._id}`,
      method: "PUT",
      data: {
        title: record?.title || "",
        description: record?.description || "",
        category_ids: normalizeIds(record?.category_ids),
        sub_category_ids: normalizeIds(record?.sub_category_ids),
        occasion_ids: normalizeIds(record?.occasion_ids),
        keywords_ids: normalizeIds(record?.keywords_ids),
        productType: record?.productType || null,
        has_variants: record?.has_variants ?? false,
        isCODAvailable: record?.isCODAvailable ?? true,
        price: record?.price ?? null,
        stock: record?.stock ?? null,
        is_active: !record?.is_active,
        digital_document_ids: normalizeIds(record?.digitalDocuments),
      },
      onSuccess: (response) => {
        ShowToast(response.message, Severty.SUCCESS);
        setRefresh((prev) => !prev);
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to update status", Severty.ERROR);
      },
    });
  };

  const onDelete = (id) => {
    requestRef.current({
      url: `${api.addEdit}/${id}`,
      method: "DELETE",
      onSuccess: (response) => {
        ShowToast(response.message, Severty.SUCCESS);
        setRefresh((prev) => !prev);
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to delete product", Severty.ERROR);
      },
    });
  };

  const getAffiliateStatus = (record) => {
    if (record?.is_affiliated || record?.affiliateLink) {
      return { label: "Completed", color: "green" };
    }

    if (record?.is_affiliate_check) {
      return { label: "Failed", color: "red" };
    }

    return { label: "Pending", color: "gold" };
  };

  const handleGenerateAffiliateLinks = () => {
    setAffiliateRunning(true);
    requestRef.current({
      url: api.affiliateRuns,
      method: "POST",
      onSuccess: (response) => {
        setAffiliateRunning(false);
        ShowToast(response?.message || "Affiliate generation completed successfully", Severty.SUCCESS);
        setRefresh((prev) => !prev);
        fetchAffiliateReport();
      },
      onError: (error) => {
        setAffiliateRunning(false);
        const message = error?.response?.data?.message || error?.message || "Failed to generate affiliate links";
        ShowToast(message, Severty.ERROR);
      },
    });
  };

  const fetchAffiliateReport = useCallback(() => {
    setReportsLoading(true);
    requestRef.current({
      url: api.affiliateStatus,
      method: "GET",
      onSuccess: ({ data }) => {
        setAffiliateReport(data);
        setReportsLoading(false);
      },
      onError: (error) => {
        setReportsLoading(false);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load affiliate report", Severty.ERROR);
      },
    });
  }, [api.affiliateStatus]);

  const openReports = () => {
    setReportsVisible(true);
    fetchAffiliateReport();
  };

  const columns = [
    {
      title: lang("Title"),
      dataIndex: "title",
      key: "title",
      render: (_, { title }) => {
        const fullTitle = String(title || "");
        return (
          <span className="cap" title={fullTitle || "-"}>
            {truncateTitle(fullTitle)}
          </span>
        );
      },
    },
    {
      title: lang("Product Type"),
      dataIndex: "productType",
      key: "productType",
      render: (_, { productType }) => <span className="cap">{productType || "-"}</span>,
    },
    {
      title: lang("Categories"),
      dataIndex: "category_ids",
      key: "category_ids",
      render: (_, { category_ids }) => {
        const labels = Array.isArray(category_ids) ? category_ids.map((item) => item?.name || item?.title || item).filter(Boolean) : [];
        return <span className="cap">{labels.length ? labels.join(", ") : "-"}</span>;
      },
    },
    {
      title: lang("Sub Categories"),
      dataIndex: "sub_category_ids",
      key: "sub_category_ids",
      render: (_, { sub_category_ids }) => {
        const labels = Array.isArray(sub_category_ids) ? sub_category_ids.map((item) => item?.name || item?.title || item).filter(Boolean) : [];
        return <span className="cap">{labels.length ? labels.join(", ") : "-"}</span>;
      },
    },
    {
      title: lang("Occasions"),
      dataIndex: "occasion_ids",
      key: "occasion_ids",
      render: (_, { occasion_ids }) => {
        const labels = Array.isArray(occasion_ids) ? occasion_ids.map((item) => item?.name || item?.title || item).filter(Boolean) : [];
        return <span className="cap">{labels.length ? labels.join(", ") : "-"}</span>;
      },
    },
    {
      title: lang("Keywords"),
      dataIndex: "keywords_ids",
      key: "keywords_ids",
      render: (_, { keywords_ids }) => {
        const labels = Array.isArray(keywords_ids) ? keywords_ids.map((item) => item?.name || item?.title || item?.slug || item).filter(Boolean) : [];
        return <span className="cap">{labels.length ? labels.join(", ") : "-"}</span>;
      },
    },
    {
      title: lang("Price"),
      dataIndex: "price",
      key: "price",
      render: (_, { price }) => <span>{price ?? "-"}</span>,
    },
    {
      title: lang("Stock"),
      dataIndex: "stock",
      key: "stock",
      render: (_, { stock }) => <span>{stock ?? "-"}</span>,
    },
    {
      title: lang("Variants"),
      dataIndex: "has_variants",
      key: "has_variants",
      render: (_, { has_variants }) => <span>{has_variants ? lang("Yes") : lang("No")}</span>,
    },
    {
      title: lang("COD"),
      dataIndex: "isCODAvailable",
      key: "isCODAvailable",
      render: (_, { isCODAvailable }) => <span>{isCODAvailable === false ? lang("No") : lang("Yes")}</span>,
    },
    {
      title: lang("Affiliate"),
      key: "affiliate_status",
      render: (_, record) => {
        const status = getAffiliateStatus(record);
        return <Tag color={status.color}>{lang(status.label)}</Tag>;
      },
    },
    {
      title: lang("Provider"),
      dataIndex: "affiliate_source",
      key: "affiliate_source",
      render: (_, { affiliate_source }) => <span className="cap">{affiliate_source || "-"}</span>,
    },
    {
      title: lang("Status"),
      key: "is_active",
      dataIndex: "is_active",
      render: (_, record) => <Switch checked={record?.is_active} onChange={() => handleChangeStatus(record)} />,
    },
    {
      title: lang("Action"),
      key: "action",
      render: (_, record) => (
        <div className="d-flex justify-contenbt-start">
          <Tooltip title={lang("View")} color={"purple"} key={`view-${record?._id}`}>
            <Button
              title={lang("View")}
              className="edit-cls btnStyle primary_btn"
              onClick={() => navigate(`/inventory/products/${record?._id}`)}
            >
              <EyeOutlined />
            </Button>
          </Tooltip>

          <Tooltip title={lang("Edit")} color={"purple"} key={`edit-${record?._id}`}>
            <Button
              title={lang("Edit")}
              className="edit-cls btnStyle primary_btn"
              onClick={() => {
                setSelected(record);
                setVisible(true);
              }}
            >
              <img src={EditIcon} alt="" />
            </Button>
          </Tooltip>

          <Tooltip title={lang("Delete")} color={"purple"} key={`delete-${record?._id}`}>
            <Button
              title={lang("Delete")}
              className="btnStyle deleteDangerbtn"
              onClick={() => {
                setSelected(record);
                showDeleteModal(true);
              }}
            >
              <img src={deleteWhiteIcon} alt="" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <SectionWrapper
        cardHeading={heading}
        extra={
          <div className="w-100 text-head_right_cont flex-wrap gap-2">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 12,
                width: "100%",
              }}
            >
              <div className="pageHeadingSearch">
                <Input.Search className="searchInput" placeholder={lang("Search by title or description")} onChange={(e) => setSearchText(e.target.value)} allowClear />
              </div>

              <Button
                className="primary_btn btnStyle"
                icon={<MenuOutlined />}
                onClick={() => setActionsVisible((current) => !current)}
                title={actionsVisible ? lang("Hide Actions") : lang("Show Actions")}
                style={{
                  minWidth: 56,
                  color: "#fff",
                  background: "#ef4444",
                  borderColor: "#dc2626",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              />
            </div>
          </div>
        }
      >
        {actionsVisible ? (
          <div
            className="d-flex flex-wrap gap-2 mb-3"
            style={{
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <Button className="primary_btn btnStyle" onClick={openReports}>
              <BarChartOutlined />
              {lang("Reports")}
            </Button>

            <Button
              className="primary_btn btnStyle"
              onClick={() => {
                setScrapeVisible(true);
              }}
            >
              {lang("Scrape & Add Product")}
            </Button>

            <Button
              className="primary_btn btnStyle"
              onClick={() => {
                setSelected(undefined);
                setVisible(true);
              }}
            >
              <span className="add-Ic">
                <img src={Plus} alt="" />
              </span>
              {lang("Add New Product")}
            </Button>
          </div>
        ) : null}

        <h4 className="text-right mb-1cont-space cont-space">{ShowTotal(pagination.total || 0)}</h4>
        <div className="table-responsive customPagination checkBoxSrNo">
          <Table
            loading={loading}
            columns={columns}
            dataSource={list}
            pagination={{
              current: pagination.current,
              defaultPageSize: 10,
              responsive: true,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSize: pagination.pageSize,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            onChange={(nextPagination) => fetchData(nextPagination)}
            className="ant-border-space keyword-emotion-table"
          />
        </div>
      </SectionWrapper>

      <Drawer
        title={lang("Affiliate Reports")}
        placement="right"
        width={420}
        open={reportsVisible}
        onClose={() => setReportsVisible(false)}
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchAffiliateReport} loading={reportsLoading}>
            {lang("Refresh")}
          </Button>
        }
      >
        <Spin spinning={reportsLoading}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic title={lang("Total Products")} value={affiliateReport?.stats?.totalProducts || 0} />
            </Col>
            <Col span={12}>
              <Statistic title={lang("Affiliated")} value={affiliateReport?.stats?.affiliatedProducts || 0} valueStyle={{ color: "#3f8600" }} />
            </Col>
            <Col span={12}>
              <Statistic title={lang("Not Affiliated")} value={affiliateReport?.stats?.notAffiliatedProducts || 0} valueStyle={{ color: "#cf1322" }} />
            </Col>
            <Col span={12}>
              <Statistic title={lang("Checked Failed")} value={affiliateReport?.stats?.checkedNotAffiliatedProducts || 0} valueStyle={{ color: "#fa8c16" }} />
            </Col>
            <Col span={12}>
              <Statistic title={lang("Unchecked")} value={affiliateReport?.stats?.uncheckedProducts || 0} valueStyle={{ color: "#1890ff" }} />
            </Col>
            <Col span={12}>
              <Statistic title={lang("Running")} value={affiliateReport?.isRunning ? lang("Yes") : lang("No")} />
            </Col>
          </Row>

          <div className="mt-4 pt-3">
            <Button
              block
              loading={affiliateRunning}
              disabled={affiliateRunning || affiliateReport?.isRunning}
              className="primary_btn btnStyle"
              onClick={handleGenerateAffiliateLinks}
            >
              <LinkOutlined />
              {lang("Generate Affiliate Links")}
            </Button>
          </div>
        </Spin>
      </Drawer>

      {visible && (
        <AddForm
          api={api}
          show={visible}
          hide={() => {
            setSelected(undefined);
            setVisible(false);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
          categoryOptions={categoryOptions}
          subCategoryOptions={subCategoryOptions}
          occasionOptions={occasionOptions}
          keywordOptions={keywordOptions}
          digitalDocumentOptions={digitalDocumentOptions}
        />
      )}

      {scrapeVisible && (
        <ScrapeProductModal
          api={api}
          show={scrapeVisible}
          hide={() => {
            setScrapeVisible(false);
          }}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}

      {deleteModal && (
        <DeleteModal
          reasons={[]}
          title={lang("Delete Product")}
          subtitle={lang("This action will permanently remove the record. Are you sure?")}
          show={deleteModal}
          hide={() => {
            showDeleteModal(false);
            setSelected(undefined);
          }}
          onOk={() => onDelete(selected?._id)}
        />
      )}
    </>
  );
}

export default Index;
